<script>
import CustomersApi from "@/services/customers-api";
import OrdersApi from "@/services/orders-api";

export default {
    props: {
        firstDay: {
            type: String,
            default: null
        },
        lastDay: {
            type: String,
            default: null
        },
        // supplier view: adds the filter by customer
        showCustomerFilter: {
            type: Boolean,
            default: false
        },
    },

    data() {
        return {
            grid: [],
            customersList: [],
            selected: null,
            start: this.firstDay,
            end: this.lastDay,
            isTableLoading: false,
            pagemodel: { currentPage: 1, perPage: 10, totalRows: 0, pageSize: 10 },
            fields: [
                { key: 'fileName', label: 'Fichier' },
                { key: 'customerName', label: 'Client' },
                { key: 'uploadedAt', label: 'Date de réception' },
                { key: 'failedLinesCount', label: 'Lignes en erreur' },
                { key: 'status', label: 'Statut' },
            ],
            lineFields: [
                { key: 'itemRef', label: 'Référence' },
                { key: 'quantity', label: 'Quantité' },
                { key: 'errorMessage', label: 'Erreur' },
            ],
            selectedFile: null,
        }
    },

    mounted() {
        if (this.showCustomerFilter) {
            this.getCustomers();
        }
        this.getImportedOrders();
    },

    methods: {
        getImportedOrders() {
            this.isTableLoading = true
            const customerId = this.selected ? this.selected.id : null
            OrdersApi.getFtpImportedOrders(this.pagemodel.currentPage, this.pagemodel.perPage, this.start, this.end, customerId).then(
                (result) => {
                    if (result) {
                        this.grid = result.list;
                        this.pagemodel.totalRows = result.total;
                        this.pagemodel.currentPage = result.page;
                        this.pagemodel.perPage = result.pageSize;
                        this.pagemodel.pageSize = result.pageSize;
                    }
                }
            ).finally(() => {
                this.isTableLoading = false
            })
        },

        getCustomers() {
            CustomersApi.getCustomers().then(
                response => {
                    this.customersList = response;
                }
            )
        },

        // filters changed: back to the first page
        refresh() {
            this.pagemodel.currentPage = 1
            this.getImportedOrders()
        },

        showLineErrors(file) {
            this.selectedFile = file
            this.$bvModal.show('ftp-line-errors-modal')
        },

        formatDate(value) {
            if (!value) return ''
            return new Date(value).toLocaleString('fr-FR')
        },

        statusLabel(status) {
            const labels = { PENDING: 'En attente', IN_PROCESS: 'En cours', RETRY: 'Nouvelle tentative', SYNCED: 'Importé', FAILED: 'Échec' }
            return labels[status] || status
        },

        statusVariant(status) {
            const variants = { PENDING: 'dark', IN_PROCESS: 'dark', RETRY: 'warning', SYNCED: 'success', FAILED: 'danger' }
            return variants[status] || 'secondary'
        },
    },

    watch: {
        selected() {
            this.refresh()
        },
        end() {
            if ((this.end && this.start) || (!this.end && !this.start)) {
                this.refresh()
            }
        },
        start() {
            if ((this.end && this.start) || (!this.end && !this.start)) {
                this.refresh()
            }
        },
    }
};
</script>
<template>
<div class="mt-2 mb-2 h-100">
    <div class="p-0">
        <div class="clearfix m-4">
            <h4 class="card-title mb-4 font-size-18 text-dark">Commandes importées par application pharmaceutique</h4>

            <div v-if="showCustomerFilter" class="row pt-3">
                <div class="col-md-12 pr-2 pt-2 pt-md-0">
                    <b-form-group label="" label-for="">
                        <v-select :options="customersList" :placeholder="$t('lists.customers.label')" label="fullCompanyName" v-model="selected">
                            <span slot="no-options">{{$t('lists.emptyLists.label')}}</span>
                        </v-select>
                    </b-form-group>
                </div>
            </div>
            <div class="row">
                <div class="col-md-6 pr-2">
                    <b-form-group>
                        <b-form-datepicker
                          v-model="start"
                          locale="fr"
                          label-no-date-selected="Date Début"
                          dark
                          reset-button
                          close-button
                          label-reset-button="Réinitialiser"
                          label-close-button="Fermer"
                          dropright
                          hide-header
                        ></b-form-datepicker>
                    </b-form-group>
                </div>
                <div class="col-md-6 pr-2">
                    <b-form-group>
                        <b-form-datepicker
                          v-model="end"
                          locale="fr"
                          label-no-date-selected="Date Fin"
                          dark
                          reset-button
                          close-button
                          label-reset-button="Réinitialiser"
                          label-close-button="Fermer"
                          dropright
                          hide-header
                        ></b-form-datepicker>
                    </b-form-group>
                </div>
            </div>
        </div>

        <div class="table-responsive p-4" style="min-height: 30em;">
            <b-table :items="grid" :fields="fields" :busy="isTableLoading" show-empty empty-text="Aucune ligne dans le tableau" hover responsive class="mb-0">
                <template v-slot:cell(customerName)="data">
                    {{ data.item.customerName }}
                </template>
                <template v-slot:cell(uploadedAt)="data">
                    {{ formatDate(data.item.uploadedAt) }}
                </template>
                <template v-slot:cell(failedLinesCount)="data">
                    <b-button v-if="data.item.failedLinesCount > 0" size="sm" variant="outline-danger" @click="showLineErrors(data.item)">
                        <i class="fas fa-eye mr-2"></i>Voir  <span>({{ data.item.failedLinesCount }})</span> 
                    </b-button>
                </template>
                <template v-slot:cell(status)="data">
                    <b-badge :variant="statusVariant(data.item.status)">{{ statusLabel(data.item.status) }}</b-badge>
                    <div class="text-danger "><i>{{data.item.errorMessage  }}</i></div>
                </template>
                <template v-slot:table-busy>
                    <div class="d-flex justify-content-center">
                        <div class="spinner-border text-success" role="status">
                            <span class="sr-only">Loading...</span>
                        </div>
                    </div>
                </template>
            </b-table>
        </div>

        <!-- Pagination -->
        <div class="row pt-3 pl-4 pr-4 pb-4">
            <div class="col-sm-8">
                <div class="dataTables_paginate paging_simple_numbers float-left d-none d-md-flex">
                    <b-form-select @input="refresh()" v-model="pagemodel.perPage" size="sm" style="width: 5em" :options="[10,15,50,100]"></b-form-select>
                    <div class="d-none d-lg-inline pt-1"><span class="text-muted mb-0 ml-1">sur </span><span class="font-weight-bold"> {{pagemodel.totalRows}} lignes</span></div>
                </div>
            </div>
            <div class="col-sm-4">
                <div class="dataTables_paginate paging_simple_numbers float-right">
                    <ul class="pagination pagination-rounded mb-0">
                        <b-pagination v-model="pagemodel.currentPage" :per-page="pagemodel.pageSize" :total-rows="pagemodel.totalRows" @input="getImportedOrders()" :disabled="isTableLoading"></b-pagination>
                    </ul>
                </div>
            </div>
        </div>
        <!-- Fin Pagination -->



        <b-modal id="ftp-line-errors-modal" size="lg" scrollable hide-footer :title="'Lignes en erreur' + (selectedFile ? ' - ' + selectedFile.fileName : '')">
            <b-table :items="selectedFile ? selectedFile.lineErrors : []" :fields="lineFields" show-empty empty-text="Aucune ligne en erreur" small striped responsive></b-table>
        </b-modal>
    </div>
</div>
</template>
